/* DelaFuente — Diamond data layer (server-side, Storefront API).
 * Replaces the static window.__DLF_DIAMONDS array with on-demand, server-filtered,
 * cursor-paginated queries so the picker scales from hundreds to tens of thousands
 * of stones without shipping the whole catalog to the browser.
 *
 * Filtering is pushed to Shopify: facets are matched by TAG (shape/color/clarity/cut/lab
 * and carat buckets) written by the Nivoda sync; sorting/pagination use the native
 * products(sortKey: PRICE, first, after) connection. Metafields (specs/cert/video) are
 * read back for the detail view and MUST be exposed to the Storefront API (definitions
 * with storefront read access) for these to return.
 *
 * Dormant until a Storefront token is present in #dlf-dp-config (config.storefrontToken).
 * Exposes window.DLF_DIAMOND_API = { enabled, query(opts) -> Promise<{items,pageInfo}> }.
 */
(function () {
  'use strict';

  var cfgEl = document.getElementById('dlf-dp-config');
  var CFG = {};
  try { CFG = JSON.parse((cfgEl && cfgEl.textContent) || '{}'); } catch (e) { CFG = {}; }

  var SHOP = CFG.shopDomain || (location.host);
  var TOKEN = CFG.storefrontToken || '';
  var API_VERSION = CFG.storefrontApiVersion || '2024-10';
  var PRODUCT_TYPE = CFG.diamondProductType || 'Loose Diamond';
  var SHAPE = CFG.diamondShape || 'Oval';
  var PAGE_SIZE = CFG.diamondPageSize || 24;
  // Market country (e.g. "MX", "ES", "US"). When set, prices are fetched @inContext so they
  // come back in the market's currency — otherwise the shop default (USD) would show on /es-mx.
  var COUNTRY = String(CFG.country || '').trim().toUpperCase();
  var HAS_CTX = /^[A-Z]{2}$/.test(COUNTRY);

  var ENABLED = !!TOKEN;

  /* UI carat-range label -> tag written by the sync (ct:<lo>-<hi> / ct:3.0+).
   * Keep in lockstep with FILTER_DEFS Carat options in dlf-diamond-picker.js. */
  var CARAT_TAG = {
    '1.0 – 1.5 ct': 'ct:1.0-1.5',
    '1.5 – 2.0 ct': 'ct:1.5-2.0',
    '2.0 – 3.0 ct': 'ct:2.0-3.0',
    '3.0 ct +': 'ct:3.0+'
  };

  function esc(v) { return String(v == null ? '' : v).replace(/'/g, "\\'"); }
  function orGroup(tags) {
    var parts = tags.filter(Boolean).map(function (t) { return "tag:'" + esc(t) + "'"; });
    if (!parts.length) return '';
    return parts.length === 1 ? parts[0] : '(' + parts.join(' OR ') + ')';
  }

  /* Translate the picker's filterSel state into a Storefront search query string. */
  function buildQuery(filterSel) {
    var groups = [];
    groups.push("product_type:'" + esc(PRODUCT_TYPE) + "'");
    groups.push("tag:'shape:" + esc(SHAPE) + "'");
    filterSel = filterSel || {};

    var carat = (filterSel.Carat || []).map(function (lbl) { return CARAT_TAG[lbl]; });
    var g;
    if ((g = orGroup(carat))) groups.push(g);
    if ((g = orGroup((filterSel.Color || []).map(function (v) { return 'color:' + v; })))) groups.push(g);
    if ((g = orGroup((filterSel.Cut || []).map(function (v) { return 'cut:' + v; })))) groups.push(g);
    if ((g = orGroup((filterSel.Lab || []).map(function (v) { return 'lab:' + v; })))) groups.push(g);
    if ((g = orGroup((filterSel.Clarity || []).map(function (v) { return 'clarity:' + v; })))) groups.push(g);

    return groups.join(' AND ');
  }

  // @inContext(country:) is added only when a market country is known, so prices resolve in the
  // market currency (MXN/EUR/…). Without it the query still runs and returns the shop default.
  var CTX_ARG = HAS_CTX ? ',$country:CountryCode!' : '';
  var CTX_DIR = HAS_CTX ? ' @inContext(country:$country)' : '';
  var GQL = [
    'query DlfDiamonds($q:String!,$first:Int!,$after:String,$reverse:Boolean!' + CTX_ARG + ')' + CTX_DIR + '{',
    '  products(first:$first,after:$after,query:$q,sortKey:PRICE,reverse:$reverse){',
    '    pageInfo{ hasNextPage endCursor }',
    '    nodes{',
    '      id handle',
    '      featuredImage{ url }',
    '      variants(first:1){ nodes{ id price{ amount currencyCode } } }',
    '      shape: metafield(namespace:"custom",key:"shape"){ value }',
    '      carat: metafield(namespace:"custom",key:"carat"){ value }',
    '      color: metafield(namespace:"custom",key:"color"){ value }',
    '      clarity: metafield(namespace:"custom",key:"clarity"){ value }',
    '      cut: metafield(namespace:"custom",key:"cut"){ value }',
    '      lab: metafield(namespace:"custom",key:"lab"){ value }',
    '      polish: metafield(namespace:"custom",key:"polish"){ value }',
    '      symmetry: metafield(namespace:"custom",key:"symmetry"){ value }',
    '      measurements: metafield(namespace:"custom",key:"measurements"){ value }',
    '      cert: metafield(namespace:"custom",key:"certificate_number"){ value }',
    '      certPdf: metafield(namespace:"custom",key:"certificate_pdf"){ value }',
    '      video: metafield(namespace:"custom",key:"video"){ value }',
    '    }',
    '  }',
    '}'
  ].join('\n');

  function mv(node, key) { return node[key] && node[key].value != null ? node[key].value : ''; }
  function numId(gid) { var m = /(\d+)\s*$/.exec(String(gid || '')); return m ? Number(m[1]) : null; }
  // URL fields (certPdf/video/img) get injected into the DOM; accept only http(s) so a bad
  // metafield value can never become a javascript:/data: sink.
  function safeUrl(u) { var s = (u == null ? '' : String(u)).trim(); return /^https?:\/\//i.test(s) ? s : ''; }
  function mapCode(v) { var m = { EX: 'Excellent', ID: 'Ideal', VG: 'Very Good', GD: 'Good', G: 'Good', F: 'Fair', PR: 'Poor' }; return v ? (m[v] || v) : v; }

  /* Normalize a Storefront product node into the exact shape the picker consumes
   * (same keys as normReal() in dlf-diamond-picker.js). */
  function normalize(node) {
    var variant = node.variants && node.variants.nodes && node.variants.nodes[0];
    var variantId = variant ? numId(variant.id) : null;
    var price = variant && variant.price ? Number(variant.price.amount) : 0;
    var currency = variant && variant.price ? variant.price.currencyCode : null;
    var dims = String(mv(node, 'measurements')).replace(/\s*mm\s*$/i, '').replace(/\s*[x×]\s*/gi, ' × ').trim();
    return {
      id: variantId,
      variantId: variantId,
      productId: numId(node.id),
      handle: node.handle,
      ct: String(mv(node, 'carat')),
      color: mv(node, 'color'),
      clar: mv(node, 'clarity'),
      cut: mapCode(mv(node, 'cut')),
      lab: mv(node, 'lab'),
      shape: mv(node, 'shape') || SHAPE,
      polish: mapCode(mv(node, 'polish')),
      sym: mapCode(mv(node, 'symmetry')),
      dims: dims,
      price: price,
      currency: currency,
      cert: mv(node, 'cert'),
      certPdf: safeUrl(mv(node, 'certPdf')),
      video: safeUrl(mv(node, 'video')),
      img: safeUrl(node.featuredImage ? node.featuredImage.url : null)
    };
  }

  /* query({ filterSel, sort:'asc'|'desc', after:cursor|null }) -> Promise<{items, pageInfo}> */
  function query(opts) {
    opts = opts || {};
    if (!ENABLED) return Promise.reject(new Error('Storefront token not configured'));
    var variables = {
      q: buildQuery(opts.filterSel),
      first: opts.pageSize || PAGE_SIZE,
      after: opts.after || null,
      reverse: opts.sort === 'desc'
    };
    if (HAS_CTX) variables.country = COUNTRY;
    return gql(GQL, variables).then(function (json) {
      var conn = json.data && json.data.products;
      if (!conn) return { items: [], pageInfo: { hasNextPage: false, endCursor: null } };
      return {
        items: conn.nodes.map(normalize).filter(function (d) { return d.variantId; }),
        pageInfo: conn.pageInfo
      };
    });
  }

  /* Fetch a single variant's price in the market currency (used for the setting-only base so the
   * roll-up total matches the market prices of the diamonds). gid = "gid://shopify/ProductVariant/…"
   * or a numeric id. Resolves { amount:Number, currency:String } or null. */
  var VARIANT_GQL = [
    'query DlfVariantPrice($id:ID!' + CTX_ARG + ')' + CTX_DIR + '{',
    '  node(id:$id){ ... on ProductVariant { price{ amount currencyCode } } }',
    '}'
  ].join('\n');
  function variantPrice(id) {
    if (!ENABLED || id == null) return Promise.resolve(null);
    var gid = String(id).indexOf('gid://') === 0 ? String(id) : 'gid://shopify/ProductVariant/' + id;
    var variables = { id: gid };
    if (HAS_CTX) variables.country = COUNTRY;
    return gql(VARIANT_GQL, variables).then(function (json) {
      var p = json.data && json.data.node && json.data.node.price;
      return p ? { amount: Number(p.amount), currency: p.currencyCode } : null;
    });
  }

  function gql(queryStr, variables) {
    return fetch('https://' + SHOP + '/api/' + API_VERSION + '/graphql.json', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Shopify-Storefront-Access-Token': TOKEN
      },
      body: JSON.stringify({ query: queryStr, variables: variables })
    }).then(function (r) { return r.json(); }).then(function (json) {
      if (json.errors && json.errors.length) {
        throw new Error(json.errors.map(function (e) { return e.message; }).join('; '));
      }
      return json;
    });
  }

  window.DLF_DIAMOND_API = { enabled: ENABLED, pageSize: PAGE_SIZE, country: HAS_CTX ? COUNTRY : null, query: query, variantPrice: variantPrice, buildQuery: buildQuery };
})();
